import { Box, Divider, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { Control } from 'react-hook-form';

import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE } from '@/modules/entities/BusinessEntityDetailsSubform/BusinessEntityDetailsSubform.types';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrency } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';

import { DollarPercentValues } from './DollarPercentButtonGroup';
import {
  CommonHypotheticalTransferFormPaths,
  CommonHypotheticalTransferFormShape,
  FundingKind,
  FundingPattern,
} from './estateWaterfallHypotheticalTransfers.types';

interface SelectedAssetDetailsProps<
  FormShape extends CommonHypotheticalTransferFormShape,
> {
  control: Control<FormShape>;
  fundingKind: FundingKind | null;
  selectedAssetDetails: {
    displayName: string;
    totalValueForClass: Decimal;
    hasOwnershipStake: boolean | null;
    ownershipPercentage: Decimal | null;
    id: string;
  }[];
  assetsPattern: FundingPattern;
  totalSelectedValue: Decimal;
}

export function SelectedAssetDetails<
  FormShape extends CommonHypotheticalTransferFormShape,
>({
  control,
  fundingKind,
  selectedAssetDetails,
  assetsPattern,
  totalSelectedValue,
}: SelectedAssetDetailsProps<FormShape>) {
  return (
    <>
      {fundingKind === 'assets' && (
        <Box>
          <Stack spacing={1} divider={<Divider />}>
            {selectedAssetDetails.length === 0 && (
              <Box textAlign="center" py={1}>
                <Typography variant="body-xs">No assets selected</Typography>
              </Box>
            )}
            {selectedAssetDetails.map((asset) => (
              <Stack
                key={asset.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                px={2}
              >
                <Stack>
                  <Typography variant="title-s" component="div">
                    {asset.displayName}
                  </Typography>
                  {asset.hasOwnershipStake && asset.ownershipPercentage && (
                    <Typography variant="body-xs">
                      {formatPercent(
                        asset.ownershipPercentage,
                        BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE
                      )}
                      %
                    </Typography>
                  )}
                  <Typography variant="body-xs">
                    {formatCurrency(asset.totalValueForClass)}
                  </Typography>
                </Stack>
                <Box flexShrink={0} maxWidth={200}>
                  {assetsPattern === DollarPercentValues.DOLLAR && (
                    <FormAwareCurrencyInput
                      required
                      fieldName={
                        `selectedAssetClassAndBusinessValues.${asset.id}.amount` as const satisfies CommonHypotheticalTransferFormPaths
                      }
                      label="Asset value"
                      hideLabel
                      control={control}
                      isDecimalJSInput
                    />
                  )}
                  {assetsPattern === DollarPercentValues.PERCENT && (
                    <FormAwarePercentInput
                      required
                      fieldName={
                        `selectedAssetClassAndBusinessValues.${asset.id}.percent` as const satisfies CommonHypotheticalTransferFormPaths
                      }
                      label="Asset percentage"
                      hideLabel
                      control={control}
                      isDecimalJSInput
                    />
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
          <Box
            sx={{
              borderTop: '2px solid',
              borderColor: COLORS.NAVY[500],
              p: 2,
              mt: 2,
              textAlign: 'right',
            }}
          >
            <Typography variant="body-xs" fontWeight="bold">
              {formatCurrency(totalSelectedValue)}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
}
